import React, { FC, Fragment, useCallback, useState } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { getDentistsForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';
import { User, UserListFilter } from 'models/user';
import debounce from 'lodash.debounce';
import DentistList from 'components/users/dentists/dentist-list/dentist-list.component';
import AddDentistDialog from 'components/users/dentists/add-dentist-dialog/add-dentist-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import DentistPageActions from 'components/users/dentists/dentist-page-actions/dentist-page-actions.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useTranslation } from 'react-i18next';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { currentUserVar } from 'apollo/reactive-variables/user';

const Dentists: FC = () => {
    const currentUser = currentUserVar();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [addSnackbarOpen, setAddSnackbarOpen] = useState(false);
    const [filter, setFilter] = useState<UserListFilter>({ freeText: '', active: true });
    const [lastOffset, setLastOffset] = useState(0);
    const [dentists, setDentists] = useState<User[]>([]);
    const { t: translate } = useTranslation(['users']);
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        loading: dentistsLoading,
        fetchMore,
        networkStatus,
        refetch,
    } = useQuery(getDentistsForUserListTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clinicId: (currentUser.organization as Organization)?._id,
            filters: filter,
            limit: 25,
            offset: 0,
        },
        onCompleted: ({ dentists }) => {
            setDentists(dentists);
            setLastOffset(dentists.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: UserListFilter) => {
            setFilter({ freeText: freeText.toLocaleLowerCase(), active: active });
            setLastOffset(0);
        }, 800),
        [],
    );

    const handleOpenDialog = () => {
        setAddSnackbarOpen(false);
        setDialogOpen(true);
    };
    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('USERS')} />
            <DentistPageActions onFilterChange={handleFilterChange} onAddUserDialogOpen={handleOpenDialog} />
            {dentistsLoading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <Fragment>
                    <DentistList
                        filteredDentists={dentists}
                        onFetchMore={handleFetchMore}
                        dentistsLoading={dentistsLoading}
                        onUserUpdated={refetch}
                    />
                    <AddDentistDialog
                        dialogOpen={dialogOpen}
                        onDialogClose={handleCloseDialog}
                        onUserAdded={() => {
                            setAddSnackbarOpen(true);
                            refetch();
                        }}
                        onUserLinked={() => {
                            refetch();
                        }}
                    />
                    {addSnackbarOpen ? (
                        <AlertSnackbar type="success" message={translate('USER_ADDED_MESSAGE')} />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default Dentists;
